import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  ReactNode,
} from 'react';

export type Branding = {
  logoUrl: string;
  primaryColor: string;
  textOnPrimary: string;
  primaryHover: string;
  hasCustomBranding: boolean;
};

const DEFAULT_PRIMARY = '#232323';
const DEFAULT_LOGO = 'https://app.opiniion.com/images/Opiniion_Icon.png';

const defaultBranding: Branding = {
  logoUrl: DEFAULT_LOGO,
  primaryColor: DEFAULT_PRIMARY,
  textOnPrimary: '#ffffff',
  primaryHover: '#3a3a3a',
  hasCustomBranding: false,
};

const BrandingContext = createContext<Branding>(defaultBranding);

export function useBranding(): Branding {
  return useContext(BrandingContext);
}

function relativeLuminance(hex: string): number {
  const normalized = hex.replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return 0;
  const rgb = [
    parseInt(normalized.slice(0, 2), 16) / 255,
    parseInt(normalized.slice(2, 4), 16) / 255,
    parseInt(normalized.slice(4, 6), 16) / 255,
  ].map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
}

function mixHex(base: string, target: string, amount: number): string {
  const b = base.replace('#', '');
  const t = target.replace('#', '');
  const r = Math.round(
    parseInt(b.slice(0, 2), 16) * (1 - amount) +
      parseInt(t.slice(0, 2), 16) * amount
  );
  const g = Math.round(
    parseInt(b.slice(2, 4), 16) * (1 - amount) +
      parseInt(t.slice(2, 4), 16) * amount
  );
  const bl = Math.round(
    parseInt(b.slice(4, 6), 16) * (1 - amount) +
      parseInt(t.slice(4, 6), 16) * amount
  );
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${bl.toString(16).padStart(2, '0')}`;
}

function computeHoverColor(hex: string): string {
  const lum = relativeLuminance(hex);
  return lum < 0.4
    ? mixHex(hex, '#ffffff', 0.12)
    : mixHex(hex, '#000000', 0.12);
}

type Props = {
  children: ReactNode;
};

export function BrandingProvider({ children }: Readonly<Props>) {
  const branding = useMemo<Branding>(() => {
    const raw = globalThis.xprops?.listing?.branding;
    if (!raw?.primary_color || raw.primary_color === DEFAULT_PRIMARY) {
      return defaultBranding;
    }

    return {
      logoUrl: raw.logo_url || DEFAULT_LOGO,
      primaryColor: raw.primary_color,
      textOnPrimary: raw.text_on_primary || '#ffffff',
      primaryHover: computeHoverColor(raw.primary_color),
      hasCustomBranding: true,
    };
  }, []);

  useLayoutEffect(() => {
    if (!branding.hasCustomBranding) return;

    const root = document.documentElement;
    root.style.setProperty('--rg-primary-color', branding.primaryColor);
    root.style.setProperty('--rg-text-on-primary', branding.textOnPrimary);
    root.style.setProperty('--rg-primary-hover', branding.primaryHover);
  }, [branding]);

  return (
    <BrandingContext.Provider value={branding}>
      {children}
    </BrandingContext.Provider>
  );
}
